import {useMemo} from 'react'
import dayjs from 'dayjs'
import {Link, generatePath} from 'react-router-dom'
import {Virtuoso} from 'react-virtuoso'
import {createColumnHelper, flexRender, getCoreRowModel, useReactTable} from '@tanstack/react-table'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {Badge} from '@/components/ui/badge/Badge'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertCircleIcon, Download01Icon, PrinterIcon, Send01Icon} from '@/components/ui/icon/Icon'
import {
    TableBodyCell,
    TableBodyCellText,
    TableBodyRow,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellLabel
} from '@/components/ui/table-atoms/TableAtoms'
import {useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {SiarExportPreviewItem} from '@/features/projects/siar-bulk-export/types'
import {
    resolveExclusionReasonLabel,
    siarPreviewProgressStatusToBadgeVariant,
    siarPreviewProgressStatusToLabel
} from '@/features/projects/siar-bulk-export/utils'
import {
    StyledBulkSiarExportExclusionReason,
    StyledBulkSiarExportInOtherBatchIcon,
    StyledBulkSiarExportPreviewTableInner,
    StyledBulkSiarExportPreviewTableWrapper,
    StyledBulkSiarExportPreviewVirtuosoBody
} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/style'

type BulkSiarExportPreviewTableProps = {
    items: SiarExportPreviewItem[]
    isSelected: (item: SiarExportPreviewItem) => boolean
    onToggle: (item: SiarExportPreviewItem) => void
    onToggleAll: () => void
    masterChecked: boolean
    masterIndeterminate: boolean
    onLoadMore: () => void
    // Excluded view: render a simple, non-selectable list (no checkboxes, no row toggling).
    readOnly?: boolean
}

const formatDate = (value: string | null): string => (value ? dayjs(value).format('DD/MM/YYYY') : '—')
const formatPriCode = (code: number): string => `PRI-${String(code).padStart(4, '0')}`

const columnHelper = createColumnHelper<SiarExportPreviewItem>()

const VIRTUOSO_HEIGHT = 380

export const BulkSiarExportPreviewTable = ({
    items,
    isSelected,
    onToggle,
    onToggleAll,
    masterChecked,
    masterIndeterminate,
    onLoadMore,
    readOnly = false
}: BulkSiarExportPreviewTableProps) => {
    const {t} = useTranslation()

    const columns = useMemo(
        () => [
            ...(readOnly
                ? []
                : [
                      columnHelper.display({
                          id: 'select',
                          size: 40,
                          header: () => (
                              <Checkbox
                                  id="bulk-siar-export-master-checkbox"
                                  checked={masterChecked}
                                  indeterminate={masterIndeterminate}
                                  onChange={onToggleAll}
                              />
                          ),
                          cell: ({row}) => (
                              <span onClick={e => e.stopPropagation()}>
                                  <Checkbox
                                      id={`bulk-siar-export-row-${row.original.projectId}`}
                                      checked={isSelected(row.original)}
                                      onChange={() => onToggle(row.original)}
                                  />
                              </span>
                          )
                      })
                  ]),
            columnHelper.accessor('projectCode', {
                id: 'projectCode',
                size: 110,
                header: () => t('projects:bulk_siar_export_column_pri_code'),
                cell: ({row}) => (
                    <Flexbox direction="column" gap={1} style={{minWidth: 0}}>
                        <Flexbox align="center" gap={1}>
                            <Link
                                to={generatePath(routes.PROJECT_DETAILS_PROJECT_DATA.handle.path, {
                                    id: row.original.projectId
                                })}
                                onClick={e => e.stopPropagation()}
                            >
                                <TableBodyCellText
                                    style={{textDecoration: 'underline', cursor: 'pointer', fontWeight: 600}}
                                >
                                    {formatPriCode(row.original.projectCode)}
                                </TableBodyCellText>
                            </Link>
                            {row.original.inOtherBatch && (
                                <StyledBulkSiarExportInOtherBatchIcon
                                    title={t('projects:bulk_siar_export_in_other_batch_tooltip')}
                                >
                                    <AlertCircleIcon size={14} />
                                </StyledBulkSiarExportInOtherBatchIcon>
                            )}
                        </Flexbox>
                        {/* On status tabs excluded rows are mixed with normal ones, so the reason is
                            shown per row. In the dedicated excluded view the reason is identical for
                            every row and is shown once in a banner above the table instead. */}
                        {!readOnly && row.original.exclusionReason && (
                            <StyledBulkSiarExportExclusionReason>
                                {t(resolveExclusionReasonLabel(row.original.exclusionReason))}
                            </StyledBulkSiarExportExclusionReason>
                        )}
                    </Flexbox>
                )
            }),
            columnHelper.accessor('patientFullName', {
                id: 'patientFullName',
                size: 220,
                header: () => t('projects:bulk_siar_export_column_patient'),
                cell: ({getValue}) => (
                    <TableBodyCellText title={getValue()} style={{minWidth: 0, maxWidth: '100%'}}>
                        {getValue()}
                    </TableBodyCellText>
                )
            }),
            columnHelper.accessor('startDate', {
                id: 'startDate',
                size: 110,
                header: () => t('projects:start_date'),
                cell: ({getValue}) => <TableBodyCellText>{formatDate(getValue())}</TableBodyCellText>
            }),
            columnHelper.accessor('expirationDate', {
                id: 'expirationDate',
                size: 110,
                header: () => t('projects:bulk_siar_export_column_expiration'),
                cell: ({getValue}) => <TableBodyCellText>{formatDate(getValue())}</TableBodyCellText>
            }),
            columnHelper.accessor('endDate', {
                id: 'endDate',
                size: 130,
                header: () => t('projects:bulk_siar_export_column_end_date'),
                cell: ({getValue}) => <TableBodyCellText>{formatDate(getValue())}</TableBodyCellText>
            }),
            columnHelper.accessor('openingExportedAt', {
                id: 'openingExportedAt',
                size: 130,
                header: () => t('projects:bulk_siar_export_column_opening_exported_at'),
                cell: ({getValue}) => <TableBodyCellText>{formatDate(getValue())}</TableBodyCellText>
            }),
            columnHelper.accessor('openingTransmissionAt', {
                id: 'openingTransmissionAt',
                size: 130,
                header: () => t('projects:bulk_siar_export_column_opening_transmission_at'),
                cell: ({getValue}) => <TableBodyCellText>{formatDate(getValue())}</TableBodyCellText>
            }),
            columnHelper.accessor('progressStatus', {
                id: 'progressStatus',
                size: 220,
                header: () => t('projects:bulk_siar_export_column_status'),
                cell: ({getValue}) => {
                    const status = getValue()
                    return (
                        <Badge variant={siarPreviewProgressStatusToBadgeVariant[status]} size="sm">
                            {status === 'to_transmit_opening' || status === 'to_transmit_closing' ? (
                                <Send01Icon size={12} />
                            ) : status === 'to_print' ? (
                                <PrinterIcon size={12} />
                            ) : (
                                <Download01Icon size={12} />
                            )}
                            {t(siarPreviewProgressStatusToLabel[status])}
                        </Badge>
                    )
                }
            })
        ],
        [t, masterChecked, masterIndeterminate, onToggleAll, isSelected, onToggle, readOnly]
    )

    const table = useReactTable({
        data: items,
        columns,
        getCoreRowModel: getCoreRowModel()
    })

    const rows = table.getRowModel().rows
    const headerGroups = table.getHeaderGroups()

    return (
        <StyledBulkSiarExportPreviewTableWrapper>
            <StyledBulkSiarExportPreviewTableInner>
                <TableHeader>
                    {headerGroups.map(headerGroup =>
                        headerGroup.headers.map(header => (
                            <TableHeaderCell key={header.id} cellSize={header.getSize()} isPinned={false}>
                                <TableHeaderCellLabel>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </TableHeaderCellLabel>
                            </TableHeaderCell>
                        ))
                    )}
                </TableHeader>
                <StyledBulkSiarExportPreviewVirtuosoBody>
                    <Virtuoso
                        style={{height: VIRTUOSO_HEIGHT}}
                        data={rows}
                        totalCount={rows.length}
                        overscan={200}
                        endReached={onLoadMore}
                        itemContent={(_index, row) => (
                            <TableBodyRow
                                isSelected={!readOnly && isSelected(row.original)}
                                onClick={readOnly ? undefined : () => onToggle(row.original)}
                                style={{cursor: readOnly ? 'default' : 'pointer'}}
                            >
                                <Flexbox fullWidth>
                                    {row.getVisibleCells().map(cell => (
                                        <TableBodyCell key={cell.id} cellSize={cell.column.getSize()}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableBodyCell>
                                    ))}
                                </Flexbox>
                            </TableBodyRow>
                        )}
                    />
                </StyledBulkSiarExportPreviewVirtuosoBody>
            </StyledBulkSiarExportPreviewTableInner>
        </StyledBulkSiarExportPreviewTableWrapper>
    )
}
